import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { HardDrive, Inbox, LayoutDashboard, LineChart, LogOut, UserRound, Users } from "lucide-react";
import { BrandLogo } from "./BrandLogo";
import { canWrite, isAdmin, ROLE_LABELS, useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { LEGAL_NAME } from "../lib/brand";

const navCls = ({ isActive }) =>
  `inline-flex items-center gap-1.5 rounded-[12px] px-2.5 py-2 text-sm font-semibold transition-colors ${isActive ? "bg-white/10 text-white" : "text-white/75 hover:bg-white/5 hover:text-white"}`;

function usePendingCount(user) {
  const { pathname } = useLocation();
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!canWrite(user)) return undefined;
    let seq = 0;
    const refresh = (ev) => {
      const mine = ++seq;
      const known = ev?.detail?.count;
      if (typeof known === "number") return setCount(known);
      api.get("/bandeja/count").then((r) => mine === seq && setCount(r.data.count)).catch(() => mine === seq && setCount(0));
    };
    refresh();
    window.addEventListener("bandeja:changed", refresh);
    return () => window.removeEventListener("bandeja:changed", refresh);
  }, [user, pathname]);
  return canWrite(user) ? count : 0;
}

const MainNav = ({ user }) => {
  const pending = usePendingCount(user);
  return (
    <nav className="flex items-center gap-1" data-testid="main-nav">
      <NavLink to="/" end className={navCls} data-testid="nav-panel">
        <LayoutDashboard className="h-4 w-4" /> <span className="hidden sm:inline">Panel</span>
      </NavLink>
      <NavLink to="/portfolio" className={navCls} data-testid="nav-portfolio">
        <LineChart className="h-4 w-4" /> <span className="hidden sm:inline">Portfolio</span>
      </NavLink>
      {pending > 0 && (
        <NavLink to="/por-clasificar" className={navCls} data-testid="nav-por-clasificar">
          <Inbox className="h-4 w-4" /> <span className="hidden sm:inline">Por clasificar</span> ({pending})
        </NavLink>
      )}
      {isAdmin(user) && (
        <NavLink to="/usuarios" className={navCls} data-testid="nav-usuarios">
          <Users className="h-4 w-4" /> <span className="hidden sm:inline">Usuarios</span>
        </NavLink>
      )}
      {isAdmin(user) && (
        <NavLink to="/google-drive" className={navCls} data-testid="nav-google-drive">
          <HardDrive className="h-4 w-4" /> <span className="hidden sm:inline">Google Drive</span>
        </NavLink>
      )}
    </nav>
  );
};

export const AppHeader = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const onLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <header className="sticky top-0 z-50 border-b border-white/10 bg-[#0b1220]/95 backdrop-blur-md" data-testid="app-header">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 sm:px-6 lg:px-8">
        <Link to="/" className="flex min-w-0 items-center gap-3 text-white hover:text-white" data-testid="header-home-link">
          <BrandLogo size="h-11 w-11" testId="header-logo" />
          <span className="min-w-0 leading-tight">
            <span className="hidden text-sm font-extrabold leading-snug sm:block sm:max-w-[230px] lg:max-w-none" data-testid="header-legal-name">{LEGAL_NAME}</span>
            <span className="hidden text-xs text-white/60 sm:block">Expedientes de crédito</span>
          </span>
        </Link>
        <div className="flex items-center gap-1.5 sm:gap-4">
          <MainNav user={user} />
          <div className="hidden items-center gap-2 text-right lg:flex" data-testid="header-user">
            <UserRound className="h-4 w-4 text-[#9bc1ff]" />
            <div className="leading-tight">
              <div className="text-sm font-semibold text-white">{user?.name}</div>
              <div className="text-xs text-white/60" data-testid="header-user-role">{ROLE_LABELS[user?.role] || user?.role}</div>
            </div>
          </div>
          <button
            onClick={onLogout}
            data-testid="logout-button"
            className="tr-btn border border-white/20 px-2.5 py-2 text-sm text-white hover:border-white/40 hover:bg-white/10 hover:text-white sm:px-4"
          >
            <LogOut className="h-4 w-4" />
            <span className="hidden sm:inline">Cerrar sesión</span>
            <span className="sr-only sm:hidden">Cerrar sesión</span>
          </button>
        </div>
      </div>
    </header>
  );
};

export const AppShell = ({ children }) => (
  <div className="min-h-screen bg-[#f5f8fc]">
    <AppHeader />
    <main className="mx-auto max-w-7xl space-y-6 px-4 py-6 sm:space-y-8 sm:px-6 sm:py-10 lg:px-8">{children}</main>
  </div>
);
